/**
 * PageManager - renaming, reordering, duplicating and deleting the pages of
 * the active project, drawn in Navigate's Pages section (2026-10-05; it was
 * its own window until then). All edits commit live (no separate save), matching the
 * MacrosEditor idiom but without the cancel-discards-draft path —
 * page metadata is light enough to mutate directly.
 *
 * Drag-reorder uses pointer events on the [≡] handle. Tracking lives
 * in the modal's local state during the drag so the page list doesn't
 * rewrite localStorage on every frame; the final order commits on
 * pointerup via reorderPages().
 */

import { createMemo, createSignal, For, Show } from 'solid-js';
import {
  pagesOfActive,
  activeProject,
  renamePage,
  duplicatePage,
  deletePage,
  reorderPages,
  cyclePageColor,
  switchPage,
  type Page,
} from '../layout/projectStore.ts';

export function PageManager() {
  // Drag-reorder state. `dragIx` = source index of the row currently
  // being dragged; `hoverIx` = where the drop indicator should appear.
  // Both null when no drag is in progress. We keep page identity stable
  // and only call reorderPages() once, on pointerup, so localStorage
  // isn't written on every frame.
  const [dragIx, setDragIx] = createSignal<number | null>(null);
  const [hoverIx, setHoverIx] = createSignal<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = createSignal<string | null>(null);

  const pages = pagesOfActive;
  const activeId = createMemo(() => activeProject()?.activePageId ?? null);

  // ─── Drag reorder ────────────────────────────────────────────
  // The [≡] handle on each row owns the pointer. We capture so move/up
  // events keep firing even if the pointer leaves the row. hoverIx is
  // updated by walking the page-row rects on each move — accurate
  // enough on iPad and laptop, no need for the HTML5 drag API.
  function onHandlePointerDown(ix: number, ev: PointerEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    setDragIx(ix);
    setHoverIx(ix);
    const target = ev.currentTarget as HTMLElement;
    target.setPointerCapture(ev.pointerId);

    const listEl = target.closest('.pm-page-list') as HTMLElement | null;

    const onMove = (e: PointerEvent) => {
      if (!listEl) return;
      const rows = Array.from(listEl.querySelectorAll<HTMLElement>('.pm-page-row'));
      // Find the row whose vertical midpoint the pointer is closest to.
      let bestIx = 0;
      let bestDist = Infinity;
      rows.forEach((row, i) => {
        const r = row.getBoundingClientRect();
        const mid = r.top + r.height / 2;
        const dist = Math.abs(e.clientY - mid);
        if (dist < bestDist) {
          bestDist = dist;
          bestIx = i;
        }
      });
      setHoverIx(bestIx);
    };
    const onUp = () => {
      try { target.releasePointerCapture(ev.pointerId); } catch { /* ignore */ }
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
      const from = dragIx();
      const to = hoverIx();
      if (from !== null && to !== null && from !== to) {
        reorderPages(from, to);
      }
      setDragIx(null);
      setHoverIx(null);
    };
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  }

  function onDelete(p: Page): void {
    // Two-tap confirm to match ProjectBrowser's delete UX (the project
    // delete button uses the same pattern). Auto-clears after 3s.
    if (confirmDeleteId() === p.id) {
      setConfirmDeleteId(null);
      deletePage(p.id);
      return;
    }
    setConfirmDeleteId(p.id);
    setTimeout(() => {
      if (confirmDeleteId() === p.id) setConfirmDeleteId(null);
    }, 3000);
  }

  // The list, shared by the window and Navigate's Pages pane (2026-10-05).
  const body = () => (<>
          <div class="nav-well pm-page-list">
            <For each={pages()}>
              {(p, i) => (
                <div
                  class="nav-lr pm-page-row"
                  data-active={p.id === activeId() ? 'true' : 'false'}
                  data-dragging={dragIx() === i() ? 'true' : 'false'}
                  data-drop-hover={
                    dragIx() !== null && hoverIx() === i() && dragIx() !== i()
                      ? 'true'
                      : 'false'
                  }
                >
                  <button
                    type="button"
                    class="pm-drag-handle"
                    onPointerDown={(e) => onHandlePointerDown(i(), e)}
                    aria-label="Drag to reorder"
                    title="Drag to reorder"
                  >
                    ≡
                  </button>
                  <span class="pm-page-num">{i() + 1}.</span>
                  <span class="nv-fld pm-name-fld">
                    <PageNameInput
                      page={p}
                      onFocusSwitch={() => {
                        // Clicking the name field also switches to this page so
                        // the user can preview what they're renaming.
                        if (p.id !== activeId()) switchPage(p.id);
                      }}
                    />
                  </span>
                  <button
                    type="button"
                    class="pm-swatch"
                    style={{ background: `var(--page-hue-${p.color})` }}
                    onClick={() => cyclePageColor(p.id)}
                    title={`Colour: ${p.color}. Tap for the next one`}
                    aria-label="Cycle page color"
                  />
                  <button
                    type="button"
                    class="nv-b nv-b-q nv-b-sm pm-action"
                    onClick={() => duplicatePage(p.id)}
                    title="Duplicate this page"
                    aria-label="Duplicate"
                  >
                    ⧉
                  </button>
                  <button
                    type="button"
                    class="nv-b nv-b-q nv-b-sm pm-action pm-action-danger"
                    data-confirm={confirmDeleteId() === p.id ? 'true' : 'false'}
                    onClick={() => onDelete(p)}
                    disabled={pages().length === 1}
                    title={
                      pages().length === 1
                        ? 'Can\'t delete the last page'
                        : confirmDeleteId() === p.id
                          ? 'Tap again to confirm'
                          : 'Delete this page'
                    }
                    aria-label="Delete"
                  >
                    {confirmDeleteId() === p.id ? 'Delete?' : '×'}
                  </button>
                </div>
              )}
            </For>
          </div>
          <Show when={pages().length === 0}>
            <p class="nav-empty pm-empty">No project is open. Pick one in Project, or start a new one.</p>
          </Show>
  </>);
  return <div class="nav-pane-body pm-body" role="region" aria-label="Pages">{body()}</div>;
}

/** Page name field — draft-and-commit pattern (mirrors InspectorPanel's
 *  NameField). Keystrokes update a local signal only; the store gets one
 *  write on blur or Enter.
 *
 *  Without this, every keystroke called `renamePage()` directly, and
 *  `renamePage()` always spreads a fresh `{...pg, name}` object even when
 *  the value hasn't changed — so the touched page's object identity
 *  changed on every keystroke. Solid's `<For>` tracks items by reference,
 *  so a changed reference at the same index tears down and rebuilds that
 *  row's DOM, dropping input focus after the first character (confirmed
 *  empirically: typing "Hello" landed as "Page 1H"). It also pushed one
 *  undo-history entry per keystroke instead of per edit. */
function PageNameInput(props: { page: Page; onFocusSwitch: () => void }) {
  const [draft, setDraft] = createSignal(props.page.name);

  function commit() {
    const v = draft();
    if (v === props.page.name) return;
    renamePage(props.page.id, v);
  }

  return (
    <input
      class="pm-page-name"
      type="text"
      value={draft()}
      onInput={(e) => setDraft(e.currentTarget.value)}
      onBlur={commit}
      onClick={props.onFocusSwitch}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        commit();
        (e.currentTarget as HTMLInputElement).blur();
      }}
    />
  );
}
